/**
 * Transfer Sankey Layout Algorithm
 *
 * Layout calculations for the Transfer Sankey visualization.
 * Positions source nodes on the left, target nodes on the right,
 * and routes bezier curves between them.
 *
 * WP-FLOW-REFACTOR-2 Phase 3: Transfer Sankey Frontend
 */

import type {
  TransferSankeyNode,
  TransferSankeyEdge,
  TransferLayoutConfig,
  TransferLayoutResult,
  PositionedTransferNode,
  PositionedTransferEdge,
} from './TransferSankeyTypes';

// ============================================================================
// Layout Constants
// ============================================================================

/**
 * Default layout configuration
 */
export const DEFAULT_TRANSFER_LAYOUT_CONFIG: TransferLayoutConfig = {
  width: 600,
  height: 200,
  nodeWidth: 120,
  columnGap: 200,
  nodeMinHeight: 30,
  nodeMaxHeight: 100,
  nodePadding: 12,
};

// ============================================================================
// Main Layout Function
// ============================================================================

/**
 * Calculate layout for the transfer Sankey diagram
 *
 * @param sourceNodes - Source nodes (accounts sending money)
 * @param targetNodes - Target nodes (accounts receiving money)
 * @param edges - Edges connecting source to target
 * @param config - Layout configuration
 * @returns Positioned nodes and edges with calculated paths
 */
export function layoutTransferSankey(
  sourceNodes: TransferSankeyNode[],
  targetNodes: TransferSankeyNode[],
  edges: TransferSankeyEdge[],
  config: TransferLayoutConfig
): TransferLayoutResult {
  // Handle empty case
  if (sourceNodes.length === 0 && targetNodes.length === 0) {
    return {
      sourceColumn: [],
      targetColumn: [],
      edges: [],
      totalHeight: config.nodeMinHeight,
    };
  }

  // Calculate total amounts for proportional sizing
  const sourceTotalAmount = sourceNodes.reduce((sum, n) => sum + n.totalAmount, 0);
  const targetTotalAmount = targetNodes.reduce((sum, n) => sum + n.totalAmount, 0);

  // Calculate minimum heights needed based on node counts
  const sourceMinHeight = sourceNodes.length * (config.nodeMinHeight + config.nodePadding);
  const targetMinHeight = targetNodes.length * (config.nodeMinHeight + config.nodePadding);
  const availableHeight = Math.max(config.height, sourceMinHeight, targetMinHeight);

  // Position source nodes (left column)
  const sourceColumn = layoutColumn(
    sourceNodes,
    sourceTotalAmount,
    0, // x position at left edge
    availableHeight,
    config
  );

  // Position target nodes (right column)
  const targetColumn = layoutColumn(
    targetNodes,
    targetTotalAmount,
    config.width - config.nodeWidth, // x position at right edge
    availableHeight,
    config
  );

  // Route edges between nodes
  const positionedEdges = routeTransferEdges(
    edges,
    sourceColumn,
    targetColumn,
    config
  );

  // Calculate total height needed
  const totalHeight = Math.max(
    getColumnHeight(sourceColumn, config.nodePadding),
    getColumnHeight(targetColumn, config.nodePadding),
    config.nodeMinHeight * 2 // Minimum height for visual balance
  );

  return {
    sourceColumn,
    targetColumn,
    edges: positionedEdges,
    totalHeight,
  };
}

// ============================================================================
// Column Layout
// ============================================================================

/**
 * Layout nodes in a single column
 *
 * Sort order: External nodes at bottom, then by amount descending
 * Heights: Proportional to amount with min/max bounds
 *
 * @param nodes - Nodes to position
 * @param totalAmount - Total amount for calculating proportions
 * @param x - X position for the column
 * @param availableHeight - Total height available
 * @param config - Layout configuration
 * @returns Positioned nodes
 */
function layoutColumn(
  nodes: TransferSankeyNode[],
  totalAmount: number,
  x: number,
  availableHeight: number,
  config: TransferLayoutConfig
): PositionedTransferNode[] {
  if (nodes.length === 0) {
    return [];
  }

  // Sort: External nodes at bottom, then by amount descending
  const sorted = [...nodes].sort((a, b) => {
    if (a.isExternal !== b.isExternal) {
      return a.isExternal ? 1 : -1;
    }
    return b.totalAmount - a.totalAmount;
  });

  // Calculate heights proportionally
  const totalPadding = (sorted.length - 1) * config.nodePadding;
  const heightForNodes = availableHeight - totalPadding;

  let y = 0;
  const positioned: PositionedTransferNode[] = [];

  for (const node of sorted) {
    // Height proportional to amount, with min/max bounds
    let height: number;
    if (totalAmount > 0) {
      const proportion = node.totalAmount / totalAmount;
      height = proportion * heightForNodes;
    } else {
      // Equal distribution if no amounts
      height = heightForNodes / sorted.length;
    }

    // Apply bounds
    height = Math.max(config.nodeMinHeight, Math.min(config.nodeMaxHeight, height));

    positioned.push({
      node,
      x,
      y,
      width: config.nodeWidth,
      height,
    });

    y += height + config.nodePadding;
  }

  return positioned;
}

/**
 * Calculate total height of a column
 */
function getColumnHeight(
  column: PositionedTransferNode[],
  _nodePadding: number
): number {
  if (column.length === 0) return 0;

  const lastNode = column[column.length - 1];
  if (!lastNode) return 0;

  // Height is last node's y + height (no padding after last node)
  return lastNode.y + lastNode.height;
}

// ============================================================================
// Edge Routing
// ============================================================================

/**
 * Route edges between source and target nodes
 *
 * Creates bezier curves connecting the right edge of source nodes
 * to the left edge of target nodes.
 *
 * @param edges - Edges to route
 * @param sourceColumn - Positioned source nodes
 * @param targetColumn - Positioned target nodes
 * @param config - Layout configuration
 * @returns Positioned edges with SVG paths
 */
function routeTransferEdges(
  edges: TransferSankeyEdge[],
  sourceColumn: PositionedTransferNode[],
  targetColumn: PositionedTransferNode[],
  config: TransferLayoutConfig
): PositionedTransferEdge[] {
  // Build lookup maps for node positions
  const sourceMap = new Map<string, PositionedTransferNode>();
  for (const pn of sourceColumn) {
    sourceMap.set(pn.node.id, pn);
  }

  const targetMap = new Map<string, PositionedTransferNode>();
  for (const pn of targetColumn) {
    targetMap.set(pn.node.id, pn);
  }

  // Calculate max edge amount for stroke width scaling
  const maxAmount = Math.max(...edges.map((e) => e.amount), 1);
  const minStrokeWidth = 2;
  const maxStrokeWidth = 20;

  // Track edge slots for each node to stack multiple edges
  const sourceEdgeSlots = new Map<string, number>();
  const targetEdgeSlots = new Map<string, number>();

  const positionedEdges: PositionedTransferEdge[] = [];

  for (const edge of edges) {
    const sourceNode = sourceMap.get(edge.sourceNodeId);
    const targetNode = targetMap.get(edge.targetNodeId);

    if (!sourceNode || !targetNode) {
      // Skip edges with missing nodes
      continue;
    }

    // Calculate stroke width proportional to amount
    const strokeWidth =
      minStrokeWidth +
      (edge.amount / maxAmount) * (maxStrokeWidth - minStrokeWidth);

    // Get current slot offsets
    const sourceSlot = sourceEdgeSlots.get(edge.sourceNodeId) ?? 0;
    const targetSlot = targetEdgeSlots.get(edge.targetNodeId) ?? 0;

    // Calculate connection points
    const sourceX = sourceNode.x + sourceNode.width; // Right edge of source
    const sourceY = sourceNode.y + sourceNode.height / 2 + sourceSlot;

    const targetX = targetNode.x; // Left edge of target
    const targetY = targetNode.y + targetNode.height / 2 + targetSlot;

    // Update slot offsets for next edge
    sourceEdgeSlots.set(edge.sourceNodeId, sourceSlot + strokeWidth + 2);
    targetEdgeSlots.set(edge.targetNodeId, targetSlot + strokeWidth + 2);

    // Create bezier curve path
    const path = createBezierPath(sourceX, sourceY, targetX, targetY, config.columnGap);

    positionedEdges.push({
      edge,
      path,
      strokeWidth,
    });
  }

  return positionedEdges;
}

/**
 * Create a bezier curve path between two points
 *
 * Uses horizontal cubic bezier for smooth S-curve
 *
 * @param x1 - Start X
 * @param y1 - Start Y
 * @param x2 - End X
 * @param y2 - End Y
 * @param gap - Horizontal gap between columns (for control point calculation)
 * @returns SVG path string
 */
function createBezierPath(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  gap: number
): string {
  // Control points for horizontal bezier
  const controlOffset = gap * 0.5;
  const cx1 = x1 + controlOffset;
  const cy1 = y1;
  const cx2 = x2 - controlOffset;
  const cy2 = y2;

  return `M ${x1} ${y1} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${x2} ${y2}`;
}
